import { useEffect, useState } from "react";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Play, Sparkles, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTaxYear } from "@/context/TaxYearContext";
import { TaxYearSelect } from "@/components/TaxYearSelect";

function BlockRow({ block, onSave, onDelete }) {
  const [local, setLocal] = useState(block);
  const [dirty, setDirty] = useState(false);

  const update = (k, v) => {
    setLocal((l) => ({ ...l, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    await onSave(local);
    setDirty(false);
  };

  return (
    <tr>
      <td className="align-middle">
        <Input
          value={local.name}
          onChange={(e) => update("name", e.target.value)}
          className="h-8 text-xs"
          data-testid={`input-block-name-${block.id}`}
        />
      </td>
      <td className="align-middle w-20">
        <Input
          type="number"
          value={local.rate}
          onChange={(e) => update("rate", Number(e.target.value))}
          className="h-8 text-right font-mono text-xs"
          step="0.5"
        />
      </td>
      <td className="align-middle w-32">
        <Input
          type="number"
          value={local.opening_wdv}
          onChange={(e) => update("opening_wdv", Number(e.target.value))}
          className="h-8 text-right font-mono text-xs"
          data-testid={`input-opening-wdv-${block.id}`}
        />
      </td>
      <td className="align-middle w-32">
        <Input
          type="number"
          value={local.additions_180plus}
          onChange={(e) => update("additions_180plus", Number(e.target.value))}
          className="h-8 text-right font-mono text-xs"
          data-testid={`input-add-180plus-${block.id}`}
        />
      </td>
      <td className="align-middle w-32">
        <Input
          type="number"
          value={local.additions_below180}
          onChange={(e) => update("additions_below180", Number(e.target.value))}
          className="h-8 text-right font-mono text-xs"
          data-testid={`input-add-below180-${block.id}`}
        />
      </td>
      <td className="align-middle w-32">
        <Input
          type="number"
          value={local.deletions}
          onChange={(e) => update("deletions", Number(e.target.value))}
          className="h-8 text-right font-mono text-xs"
        />
      </td>
      <td className="align-middle w-32 text-right font-mono text-xs bg-amber-50">
        {formatINR(block.depreciation || 0)}
      </td>
      <td className="align-middle w-32 text-right font-mono text-xs bg-blue-50">
        {formatINR(block.closing_wdv || 0)}
      </td>
      <td className="align-middle w-20 text-right">
        {dirty && (
          <Button size="sm" onClick={save} className="h-7 text-xs bg-blue-700 hover:bg-blue-800" data-testid={`btn-save-block-${block.id}`}>
            Save
          </Button>
        )}
        <button onClick={() => onDelete(block.id)} className="ml-2 text-red-600 hover:text-red-800" data-testid={`btn-delete-block-${block.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}

function Inner() {
  const { activeId } = useCompany();
  const { ay } = useTaxYear();
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [totals, setTotals] = useState({ dep: 0, closing: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${activeId}/tax/blocks?ay=${ay}`);
      setBlocks(data);
      const dep = data.reduce((s, b) => s + Number(b.depreciation || 0), 0);
      const closing = data.reduce((s, b) => s + Number(b.closing_wdv || 0), 0);
      setTotals({ dep, closing });
    } catch (err) {
      toast.error(describeAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeId) load();
  }, [activeId, ay]); // eslint-disable-line react-hooks/exhaustive-deps

  const seed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/blocks/seed?ay=${ay}`);
      toast.success(`Seeded ${data.seeded} default blocks`);
      await load();
    } catch (err) {
      toast.error(describeAxiosError(err));
    } finally {
      setBusy(false);
    }
  };

  const compute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/blocks/compute?ay=${ay}`);
      setBlocks(data.blocks || []);
      setTotals({ dep: data.total_depreciation, closing: data.total_closing_wdv });
      toast.success(`Depreciation computed: ₹${formatINR(data.total_depreciation)}`);
    } catch (err) {
      toast.error(describeAxiosError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveBlock = async (block) => {
    try {
      await api.put(`/companies/${activeId}/tax/blocks/${block.id}`, {
        name: block.name,
        rate: Number(block.rate) || 0,
        opening_wdv: Number(block.opening_wdv) || 0,
        additions_180plus: Number(block.additions_180plus) || 0,
        additions_below180: Number(block.additions_below180) || 0,
        deletions: Number(block.deletions) || 0,
        notes: block.notes || "",
      });
      toast.success("Block updated");
      await load();
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  const deleteBlock = async (bid) => {
    if (!window.confirm("Delete this depreciation block?")) return;
    try {
      await api.delete(`/companies/${activeId}/tax/blocks/${bid}`);
      toast.success("Block deleted");
      await load();
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  const addBlock = async () => {
    try {
      await api.post(`/companies/${activeId}/tax/blocks?ay=${ay}`, {
        name: "New Custom Block",
        rate: 15,
      });
      toast.success("Block added");
      await load();
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  return (
    <>
      <PageHeader
        title="Depreciation — Block of Assets"
        subtitle={`AY ${ay} · Section 32 (WDV method). Additions used < 180 days depreciate at half rate.`}
        actions={
          <>
            <TaxYearSelect />
            <Button variant="outline" onClick={() => navigate("/tax")} className="h-8" data-testid="btn-dep-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {blocks.length === 0 && (
              <Button onClick={seed} disabled={busy} className="h-8 bg-blue-700 hover:bg-blue-800" data-testid="btn-dep-seed">
                <Sparkles className="w-4 h-4 mr-1" /> Seed 10 default blocks
              </Button>
            )}
            <Button variant="outline" onClick={addBlock} className="h-8" data-testid="btn-dep-add">
              <Plus className="w-4 h-4 mr-1" /> Add block
            </Button>
            <Button onClick={compute} disabled={busy || !blocks.length} className="h-8 bg-emerald-700 hover:bg-emerald-800" data-testid="btn-dep-compute">
              <Play className="w-4 h-4 mr-1" /> Recompute all
            </Button>
          </>
        }
      />
      <div className="p-6 space-y-4">
        {loading ? (
          <div className="text-sm text-gray-500">Loading blocks…</div>
        ) : blocks.length === 0 ? (
          <div className="border border-dashed border-gray-300 bg-white rounded p-10 text-center">
            <Sparkles className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <h3 className="font-medium text-gray-900">No depreciation blocks yet</h3>
            <p className="text-sm text-gray-500 mt-1 mb-3">
              Seed the 10 IT-Act default blocks (Building 5%/10%, P&M 15%, Computers 40%, Intangibles 25%, etc.) to get started.
            </p>
            <Button onClick={seed} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-dep-empty-seed">
              <Sparkles className="w-4 h-4 mr-1.5" /> Seed default blocks
            </Button>
          </div>
        ) : (
          <div className="border border-gray-200 bg-white rounded overflow-x-auto">
            <table className="tx-table w-full" data-testid="dep-blocks-table">
              <thead>
                <tr>
                  <th>Block Name</th>
                  <th className="text-right">Rate %</th>
                  <th className="text-right">Opening WDV</th>
                  <th className="text-right">Add ≥ 180d</th>
                  <th className="text-right">Add &lt; 180d</th>
                  <th className="text-right">Deletions</th>
                  <th className="text-right">Depreciation</th>
                  <th className="text-right">Closing WDV</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {blocks.map((b) => (
                  <BlockRow key={b.id} block={b} onSave={saveBlock} onDelete={deleteBlock} />
                ))}
                <tr className="bg-gray-50 font-semibold text-xs">
                  <td colSpan={6} className="text-right">Total</td>
                  <td className="text-right font-mono bg-amber-100" data-testid="dep-total-depreciation">₹ {formatINR(totals.dep)}</td>
                  <td className="text-right font-mono bg-blue-100" data-testid="dep-total-closing">₹ {formatINR(totals.closing)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {blocks.length > 0 && (
          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-3 leading-relaxed">
            <strong>How it works:</strong> Depreciation on the block = (Opening WDV + Full-rate additions – Deletions) × Rate + Half-rate additions × Rate/2.
            The half-year rule applies when an asset is put to use for less than 180 days in the year. Closing WDV carries forward to next year&apos;s opening.
            The total depreciation figure is what you&apos;ll use in the Tax Computation to reduce the Net Profit as per books.
          </div>
        )}
      </div>
    </>
  );
}

export default function DepreciationBlocks() {
  return <RequireCompany><Inner /></RequireCompany>;
}
